import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Discount Coupon master (Masters → Coupons). A promo code applied to orders,
 * either a percent off or a flat amount, with redemption caps and a validity
 * window. See ADR-0016 / ADR-0026. `redemptionCount` is system-managed (bumped
 * when a coupon is redeemed) - it is read-only and NOT part of the write
 * contract. `code` is unique.
 */

/** Mirrors the Prisma `CouponKind` enum. */
export const CouponKindSchema = z.enum(['PERCENT', 'FLAT']);
export type CouponKind = z.infer<typeof CouponKindSchema>;

export const DiscountCouponListItemSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  kind: CouponKindSchema,
  /** PERCENT: 10 means 10%. FLAT: amount in the coupon's currency. */
  value: z.number(),
  /** Loose FK to `currencies` (no Prisma relation); resolve label client-side. */
  currencyId: UuidSchema.nullable(),
  minOrderValue: z.number().nullable(),
  maxRedemptions: z.number().int(),
  maxPerCustomer: z.number().int(),
  /** System-managed running count of redemptions. Read-only. */
  redemptionCount: z.number().int(),
  validFrom: IsoDateTimeSchema.nullable(),
  validUntil: IsoDateTimeSchema.nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type DiscountCouponListItem = z.infer<typeof DiscountCouponListItemSchema>;

export const DiscountCouponListQuerySchema = PaginationQuerySchema.extend({
  kind: CouponKindSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type DiscountCouponListQuery = z.infer<typeof DiscountCouponListQuerySchema>;

export const DiscountCouponListResponseSchema = paginatedResponseSchema(
  DiscountCouponListItemSchema,
);
export type DiscountCouponListResponse = z.infer<typeof DiscountCouponListResponseSchema>;

export const DiscountCouponDetailSchema = DiscountCouponListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type DiscountCouponDetail = z.infer<typeof DiscountCouponDetailSchema>;

const DiscountCouponCoreFieldsSchema = z
  .object({
    code: MasterCodeSchema,
    name: z.string().trim().min(1, 'Name is required').max(120),
    kind: CouponKindSchema,
    value: z.number().min(0, 'Value must be zero or more'),
    currencyId: UuidSchema.nullable().optional(),
    minOrderValue: z.number().min(0).nullable().optional(),
    maxRedemptions: z.number().int().min(0).optional(),
    maxPerCustomer: z.number().int().min(0).optional(),
    validFrom: IsoDateTimeSchema.nullable().optional(),
    validUntil: IsoDateTimeSchema.nullable().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((v) => v.kind !== 'PERCENT' || v.value <= 100, {
    message: 'A percent coupon cannot exceed 100',
    path: ['value'],
  })
  .refine(
    (v) =>
      !v.validFrom ||
      !v.validUntil ||
      new Date(v.validFrom).getTime() <= new Date(v.validUntil).getTime(),
    { message: 'Valid-until must be on or after valid-from', path: ['validUntil'] },
  );

/**
 * Create + Update share the same effects-wrapped object (refinements), so
 * Update is NOT a `.partial()` - the form always submits the full editable set.
 * `redemptionCount` is intentionally absent (system-managed).
 */
export const CreateDiscountCouponRequestSchema = DiscountCouponCoreFieldsSchema;
export type CreateDiscountCouponRequest = z.infer<typeof CreateDiscountCouponRequestSchema>;

export const UpdateDiscountCouponRequestSchema = DiscountCouponCoreFieldsSchema;
export type UpdateDiscountCouponRequest = z.infer<typeof UpdateDiscountCouponRequestSchema>;
